/**
 * Customer data form configuration.
 *
 * Please see further documentation in the `src/types.ts` file.
 */
import { ConfigurableFormConfig, JSONSchema7Object } from "../../../types";
import validateSchema from "./customerData.json";
//import * as formElements from "./formElements";
import tiffanyFormElementsSchema from "./formElements.json";
import * as formElements from "../../shared/forms/formElements";
import formElementsSchema from "../../shared/forms/formElements.json";
import { GENDER_OPTIONS } from "../fields";
import { get } from "../../../utils/helpers";

// Welcome to a configurable form!
export const formConfig: ConfigurableFormConfig = {
  // The fields are an array of `ConfigurableFormElementConfig` objects.
  // These will reference the elements which are specified in the
  // `ConfigurableFormProps.elements` prop.
  fields: [
    // Here's an example of a non-interactive element to render in the form:
    {
      // I've got a bunch of pre-defined form elements that I can spread
      // to quickly and consistently populate the field config with.
      ...formElements.sectionHeader,
      // The SectionHeader element only requires a label. This label can
      // also be automatically translated by the translation function
      // specified at `ConfigurableFormProps.t`.
      label: "customer.data.sections.personalInformation.title"
    },
    // Here is an example of an interactive element to render in the form:
    {
      ...formElements.group,
      id: "customer-name",
      props: {
        className: "customer-name"
      },
      showOnlyIf: {
        not: {
          all: {
            has: {
              "pageState.language": "ja",
              "appState.storeCountry": "JPN"
            }
          }
        }
      },
      fields: [
        {
          ...formElements.fieldTitle,
          id: "honorificPrefix",
          name: "honorificPrefix",
          label: "customer.data.fields.honorificPrefix.label",
          props: {
            required: true,
            inputProps: {
              required: true,
              clearable: true
            }
          },
          errorMessage: "customer.data.fields.honorificPrefix.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "givenName",
          name: "givenName",
          label: "customer.data.fields.givenName.label",
          props: {
            required: true,
            enterInRoman: true
          },
          errorMessage: "customer.data.fields.givenName.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "middleName",
          name: "middleName",
          label: "customer.data.fields.middleName.label"
        },
        {
          ...formElements.fieldText,
          id: "familyName",
          name: "familyName",
          label: "customer.data.fields.familyName.label",
          props: {
            required: true,
            enterInRoman: true
          },
          errorMessage: "customer.data.fields.familyName.errors.required"
        }
      ]
    },
    {
      ...formElements.group,
      id: "customer-name-jp",
      props: {
        className: "customer-name"
      },
      showOnlyIf: {
        all: {
          has: {
            "pageState.language": "ja",
            "appState.storeCountry": "JPN"
          }
        }
      },
      fields: [
        {
          ...formElements.fieldText,
          id: "familyName",
          name: "familyName",
          label: "customer.data.fields.familyName.label",
          props: {
            required: true,
            enterInRoman: true
          },
          errorMessage: "customer.data.fields.familyName.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "givenName",
          name: "givenName",
          label: "customer.data.fields.givenName.label",
          props: {
            required: true,
            enterInRoman: true
          },
          errorMessage: "customer.data.fields.givenName.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "alternateFamilyName1",
          name: "alternateFamilyName1",
          label: "customer.additionalInfo.fields.globalLastName.label",
          props: {
            required: true
          },
          errorMessage:
            "customer.additionalInfo.fields.globalLastName.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "alternateGivenName1",
          name: "alternateGivenName1",
          label: "customer.additionalInfo.fields.globalFirstName.label",
          props: {
            required: true
          },
          errorMessage:
            "customer.additionalInfo.fields.globalFirstName.errors.required"
        }
      ]
    },
    {
      ...formElements.fieldDropdownCustom,
      id: "gender",
      name: "gender",
      label: "customer.additionalInfo.fields.gender.label",
      props: {
        required: true,
        inputProps: {
          searchable: false,
          clearable: false,
          options: GENDER_OPTIONS
        }
      },
      errorMessage: "customer.additionalInfo.fields.gender.errors.required"
    },
    {
      ...formElements.fieldBirthDate,
      id: "birthDate",
      name: "birthDate",
      label: "customer.data.fields.birthDate.label",
      props: {
        inputProps: {
          labels: {
            day: "customer.data.fields.birthDate.options.day.label",
            month: "customer.data.fields.birthDate.options.month.label",
            year: "customer.data.fields.birthDate.options.year.label"
          },
          minimumAge: 16
        }
      },
      stateToProps: {
        "pageState.language": "inputProps.lang"
      },
      errorMessage: {
        _: "customer.data.fields.birthDate.errors.required",
        minimumAge:
          "customer.data.fields.birthDate.options.year.errors.underMinimumAge"
      }
    },
    {
      ...formElements.fieldEmail,
      id: "email",
      name: "email",
      label: "customer.data.fields.email.label",
      stateToProps: {
        required: (value: any, propName?: string, input?: any) =>
          get(input, "formState.isRemote")
      },
      errorMessage: {
        _: "customer.data.fields.email.errors.required",
        format: "customer.data.fields.email.errors.invalid"
      }
    },
    {
      ...formElements.group,
      id: "customer-address",
      showOnlyIf: {
        not: {
          all: {
            has: {
              "pageState.language": "ja",
              "appState.storeCountry": "JPN"
            }
          }
        }
      },
      props: {
        className: "customer-address"
      },
      fields: [
        {
          ...formElements.fieldCountry,
          id: "addressCountry",
          name: "addressCountry",
          label: "customer.data.fields.address.country.label",
          errorMessage: "customer.data.fields.address.country.errors.required",
          props: {
            required: false
          }
        },
        {
          ...formElements.fieldAutocompleteGoogleMaps,
          id: "streetAddress1",
          name: "streetAddress1",
          label: "customer.data.fields.address.line1.label",
          errorMessage: "customer.data.fields.address.line1.errors.required",
          props: {
            required: true
          },
          stateToProps: {
            "formState.addressCountry": "inputProps.country"
          }
        },
        {
          ...formElements.fieldText,
          id: "streetAddress2",
          name: "streetAddress2",
          label: "customer.data.fields.address.line2.label",
          errorMessage: "customer.data.fields.address.line2.errors.required"
        },
        {
          ...formElements.fieldText,
          id: "addressLocality",
          name: "addressLocality",
          label: "customer.data.fields.address.city.label",
          errorMessage: "customer.data.fields.address.city.errors.required",
          props: {
            required: true
          }
        },
        {
          ...formElements.fieldText,
          id: "addressRegion",
          name: "addressRegion",
          label: "customer.data.fields.address.region.label",
          errorMessage: "customer.data.fields.address.region.errors.required",
          props: {
            required: true
          }
        },
        {
          ...formElements.fieldAutocompleteGoogleMaps,
          id: "postalCode",
          name: "postalCode",
          label: "customer.data.fields.address.postCode.label",
          errorMessage: "customer.data.fields.address.postCode.errors.required",
          props: {
            required: true
          }
        }
      ]
    },
    {
      ...formElements.group,
      id: "customer-address-jp",
      showOnlyIf: {
        all: {
          has: {
            "pageState.language": "ja",
            "appState.storeCountry": "JPN"
          }
        }
      },
      props: {
        className: "customer-address"
      },
      fields: [
        {
          ...formElements.fieldAutocompleteGoogleMaps,
          id: "postalCode",
          name: "postalCode",
          label: "customer.data.fields.address.postCode.label",
          errorMessage: "customer.data.fields.address.postCode.errors.required",
          props: {
            required: true
          }
        },
        {
          ...formElements.fieldText,
          id: "addressRegion",
          name: "addressRegion",
          label: "customer.data.fields.address.region.label",
          props: {
            required: true
          }
        },
        {
          ...formElements.fieldText,
          id: "addressLocality",
          name: "addressLocality",
          label: "customer.data.fields.address.city.label",
          errorMessage: "customer.data.fields.address.city.errors.required",
          props: {
            required: true
          }
        },
        {
          ...formElements.fieldAutocompleteGoogleMaps,
          id: "streetAddress1",
          name: "streetAddress1",
          label: "customer.data.fields.address.line1.label",
          errorMessage: "customer.data.fields.address.line1.errors.required",
          props: {
            required: true
          },
          stateToProps: {
            "formState.addressCountry": "inputProps.country"
          }
        },
        {
          ...formElements.fieldText,
          id: "streetAddress2",
          name: "streetAddress2",
          label: "customer.data.fields.address.line2.label",
          errorMessage: "customer.data.fields.address.line2.errors.required"
        },
        {
          ...formElements.fieldCountry,
          id: "addressCountry",
          name: "addressCountry",
          label: "customer.data.fields.address.country.label",
          errorMessage: "customer.data.fields.address.country.errors.required",
          props: {
            required: true
          }
        }
      ]
    },
    {
      ...formElements.fieldPhone,
      id: "mobileTelephone",
      name: "mobileTelephone",
      label: "customer.data.fields.mobileTelephone.label",
      props: {
        required: true,
        inputProps: {
          labels: {
            dialCode: "customer.data.fields.phone.dialCode.label",
            phoneNumber: "customer.data.fields.mobileTelephone.label"
          }
        }
      },
      stateToProps: {
        "formState.nationality": "inputProps.country",
        "formState.addressCountry": "inputProps.country"
      },
      errorMessage: {
        _: "customer.data.fields.mobileTelephone.errors.required",
        pattern: "customer.data.fields.phone.dialCode.errors.required"
      }
    },
    {
      ...formElements.fieldPhone,
      id: "telephone",
      name: "telephone",
      label: "customer.data.fields.telephone.label",
      props: {
        inputProps: {
          maxLength: 25,
          labels: {
            dialCode: "customer.data.fields.phone.dialCode.label",
            phoneNumber: "customer.data.fields.telephone.label"
          }
        }
      },
      stateToProps: {
        "formState.nationality": "inputProps.country",
        "formState.addressCountry": "inputProps.country"
      },
      errorMessage: {
        _: "customer.data.fields.telephone.errors.required",
        pattern: "customer.data.fields.telephone.errors.required"
      }
    },
    {
      ...formElements.fieldCustomPicklist,
      id: "maritalStatus",
      name: "maritalStatus",
      label: "customer.data.fields.maritalStatus.label",
      errorMessage: "customer.data.fields.maritalStatus.errors.required"
    },
    {
      ...formElements.fieldDate,
      id: "weddingDate",
      name: "weddingDate",
      label: "customer.data.fields.weddingDate.label",
      props: {
        inputProps: {
          labels: {
            day: "customer.data.fields.birthDate.options.day.label",
            month: "customer.data.fields.birthDate.options.month.label",
            year: "customer.data.fields.birthDate.options.year.label"
          },
          numYears: 100,
          startYear: new Date().getUTCFullYear() + 2
        }
      },
      stateToProps: {
        "pageState.language": "inputProps.lang"
      },
      errorMessage: "customer.data.fields.weddingDate.errors.invalid"
    },
    {
      ...formElements.fieldText,
      id: "profession",
      name: "profession",
      label: "customer.data.fields.profession.label",
      showOnlyIf: {
        all: {
          has: {
            "pageState.language": "ja",
            "appState.storeCountry": "JPN"
          }
        }
      }
    }
  ],
  validateSchema: validateSchema as JSONSchema7Object,
  refSchemas: [
    formElementsSchema as JSONSchema7Object,
    tiffanyFormElementsSchema as JSONSchema7Object
  ],
  labels: {
    buttonBack: "general.buttons.cancel.label",
    buttonNext: "general.buttons.continue.label",
    buttonSubmit: "customer.data.buttons.next.label"
  }
};

export default formConfig;
